<script setup lang="ts">
import CanvasButton from "@/components/canvas/CanvasButton.vue";
import TextToolField from "./TextToolField.vue";
import { Z_INDEX_TOOLBAR } from "../../layouts/constants";
import { isCanvasDrawing } from "@/stores/CanvasViewState";
import { toolbarSideStart } from "@/stores/CanvasLayoutState";

/**
 * Panneau de l'outil texte réduit : le seul champ de saisie, posé sur le bord
 * bas du canvas, juste à droite de la barre d'outils. Le bouton
 * d'agrandissement rend le panneau complet, à côté de l'outil.
 */
defineProps<{
  message: string;
}>();

const emit = defineEmits<{
  open: [];
  clear: [];
  expand: [];
}>();
</script>

<template>
  <!-- Hors du menu d'options, masqué (display: none) tant que l'outil est réduit -->
  <Teleport to="body">
    <div
      class="text-tool-dock d-flex flex-row align-items-center gap-2 p-2 canvas-icon-scaled"
      :class="{ 'drawing-active': isCanvasDrawing }"
      :style="{
        left: `${toolbarSideStart}px`,
        zIndex: Z_INDEX_TOOLBAR,
      }"
      @pointerdown.stop
    >
      <TextToolField
        :message="message"
        :active="true"
        @open="emit('open')"
        @clear="emit('clear')"
      />
      <CanvasButton
        size="sm"
        phosphor-icon="arrows-out-simple"
        :tooltip-title="$t('canvas:tools.text.expand.title')"
        :tooltip-text="$t('canvas:tools.text.expand.text')"
        @click="emit('expand')"
      />
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
// Posé sur le bord bas du canvas, comme la barre de sélection : la barre
// d'outils flotte, le dock ne peut plus s'y accrocher. Coins arrondis en haut
// seulement, pas de bordure basse.
.text-tool-dock {
  position: fixed;
  bottom: 0;
  background-color: var(--canvas-bg);
  border: 1px solid var(--canvas-border);
  border-bottom: none;
  border-radius: 12px 12px 0 0;
  box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.25);
  animation: text-tool-dock-rise 0.18s ease-out;
}

@keyframes text-tool-dock-rise {
  from {
    transform: translateY(100%);
    opacity: 0.4;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>
